import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { BbIcon } from '../../components/BbIcon'
import { BbStepper } from '../../components/BbStepper'
import { useExportWizard } from '../../export/ExportWizardContext'
import { KEY_LISTS, TRANSACTION_GROUPS, iconFor } from '../import/steps'
import { EXPORT_STEPS } from './steps'

/* Step 1, ported from "batchbooks_export_step_1_synced".

   Same shape as import step 1 and driven by the same GET /api/import/entities
   data, so the grouping helpers are reused from the import steps module rather
   than duplicated.

   Unlike Import, this screen does not filter on `canImport`: everything the
   registry knows how to read is exportable, which is why it offers eight lists
   where Import offers seven (a bundle can be read but not written yet). */
export function ExportSelectTypePage() {
  const wizard = useExportWizard()
  const navigate = useNavigate()
  const [tab, setTab] = useState<'lists' | 'transactions'>(
    wizard.entity?.isTransaction ? 'transactions' : 'lists',
  )

  const inTab = wizard.entities.filter((entity) =>
    tab === 'transactions' ? entity.isTransaction : !entity.isTransaction,
  )

  let groups: Array<{ heading: string; items: typeof inTab }>
  if (tab === 'lists') {
    groups = [
      { heading: 'Key List', items: inTab.filter((e) => KEY_LISTS.includes(e.name)) },
      { heading: 'Other List', items: inTab.filter((e) => !KEY_LISTS.includes(e.name)) },
    ]
  } else {
    const named = new Set(TRANSACTION_GROUPS.flatMap((g) => g.names))
    groups = [
      ...TRANSACTION_GROUPS.map((group) => ({
        heading: group.heading,
        items: inTab.filter((e) => group.names.includes(e.name)),
      })),
      { heading: 'Other', items: inTab.filter((e) => !named.has(e.name)) },
    ]
  }

  const choose = (name: string) => {
    wizard.setEntityType(name)
    navigate('/app/export/filters')
  }

  return (
    <div className="bb bb-wizard">
      <BbStepper steps={EXPORT_STEPS} current={0} />

      <h2>Select Export Type</h2>

      <div className="bb-radios" role="radiogroup" aria-label="Export category">
        <label className="bb-radio">
          <input
            type="radio"
            name="export_type"
            value="lists"
            checked={tab === 'lists'}
            onChange={() => setTab('lists')}
          />
          Lists
        </label>
        <label className="bb-radio">
          <input
            type="radio"
            name="export_type"
            value="transactions"
            checked={tab === 'transactions'}
            onChange={() => setTab('transactions')}
          />
          Transactions
        </label>
      </div>

      {groups.map((group) =>
        group.items.length === 0 ? null : (
          <div className="bb-group" key={group.heading}>
            <h3 className="bb-group-h">{group.heading}</h3>
            <div className="bb-types">
              {group.items.map((entity) => (
                <button
                  className={entity.name === wizard.entityType ? 'bb-type bb-type-on' : 'bb-type'}
                  type="button"
                  key={entity.name}
                  aria-pressed={entity.name === wizard.entityType}
                  onClick={() => choose(entity.name)}
                >
                  <span className="bb-type-ico">
                    <BbIcon name={iconFor(entity.name)} />
                  </span>
                  {entity.label ?? entity.name}
                </button>
              ))}
            </div>
          </div>
        ),
      )}

      <div className="bb-wizard-foot">
        <Link className="bb-btn bb-btn-quiet" to="/app">
          Cancel
        </Link>
        {wizard.entityType ? (
          <Link className="bb-btn bb-btn-green" to="/app/export/filters">
            Next Step
            <BbIcon name="arrow-right" />
          </Link>
        ) : null}
      </div>
    </div>
  )
}
